import React, { FC, useEffect } from 'react';
import { Text, Box, Checkbox, Spinner, SpinnerSize } from '@bechtel/gds';
import { CraftListProps } from './types';
import { GET_CREW_MEMBERS_CREW_CHANGE_REQUEST } from '@bechtel/client-gql';
import { CrewChangeRequestMoveType, CrewMember, CrewMemberList, SearchCrewInput } from '@bechtel/types';
import { every, filter, get, some } from 'lodash';
import { ListSelectionHeadingStyled } from './styled';
import { NetworkStatus, useLazyQuery } from '@apollo/client';
import { ErrorRetry } from '@bechtel/client-common';
import CraftListItem from './item';
import { Trans, t } from '@lingui/macro';

export const CraftList: FC<CraftListProps> = ({
  generalForeman,
  superintendent,
  foreman,
  selectedCrafts,
  onCraftSelect,
  crewChangeRequestMoveType,
  onCraftDataLoaded,
}) => {
  const [getCraftList, { data: craftListData, error, loading, refetch, networkStatus, called }] = useLazyQuery<
    { crewMembers: CrewMemberList },
    { input: SearchCrewInput }
  >(GET_CREW_MEMBERS_CREW_CHANGE_REQUEST, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only', //fetchPolicy: 'network-only' to get the latest value for hasPendingCrewChangeRequest
    nextFetchPolicy: 'cache-first',
  });
  const craftList = get(craftListData, 'crewHierarchy.items', []) as CrewMemberList['items'];
  const moveEntireCrew = crewChangeRequestMoveType === CrewChangeRequestMoveType.CREW;
  const hasPendingEntireCrewChangeRequest = foreman?.hasPendingCrewChangeRequest
    ? foreman?.hasPendingCrewChangeRequest
    : false;
  const disableSelectAll = !moveEntireCrew ? every(craftList, 'hasPendingCrewChangeRequest') : false;
  const hasPendingRequestItems = some(craftList, 'hasPendingCrewChangeRequest');
  const crafItemsCanBeSelected = filter(craftList, (item: CrewMember) => {
    return item.hasPendingCrewChangeRequest !== true;
  });

  useEffect(() => {
    if (!foreman || !superintendent || !generalForeman) return;
    getCraftList({
      variables: {
        input: {
          superintendentId: superintendent?.id,
          generalForemanId: generalForeman?.id,
          foremanId: foreman?.id,
        },
      },
    });
  }, [foreman, getCraftList]);

  useEffect(() => {
    if (moveEntireCrew) {
      onCraftSelect(hasPendingRequestItems ? crafItemsCanBeSelected : craftList);
    } else {
      onCraftSelect([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moveEntireCrew, craftListData]);

  useEffect(() => {
    if (!craftList || craftList.length === 0) return;
    onCraftDataLoaded(hasPendingRequestItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [craftList, onCraftDataLoaded]);

  return (
    <>
      {(!called || networkStatus === NetworkStatus.refetch || loading) && (
        <Box
          data-testid="craft-list-loading"
          p={{ sm: '500' }}
          alignItems={{ sm: 'center' }}
          justifyContent={{ sm: 'center' }}
          flexGrow={{ sm: 1 }}
          css={craftList.length === 0 ? 'height: 100%;' : ''}
        >
          <Spinner size={SpinnerSize.LARGE} />
        </Box>
      )}
      {called && !loading && error && (
        <div data-testid="craft-list-retry">
          <ErrorRetry refetch={refetch}>
            <Trans>There was a problem loading Crew. Please click the button below to try again.</Trans>
          </ErrorRetry>
        </div>
      )}
      {called &&
        !loading &&
        !error &&
        (craftList.length === 0 ? (
          <Box
            p={{ sm: '500' }}
            alignItems={{ sm: 'center' }}
            justifyContent={{ sm: 'center' }}
            flexGrow={{ sm: 1 }}
            css={craftList.length === 0 ? 'height: 100%;' : ''}
            data-testid="craft-list-empty"
          >
            <Trans>No crafts reporting to selected foreman.</Trans>
          </Box>
        ) : (
          <Box flexDirection={{ sm: 'column' }} css="overflow:hidden">
            {!hasPendingEntireCrewChangeRequest && (
              <ListSelectionHeadingStyled
                onClick={() => {
                  if (selectedCrafts.length !== (hasPendingRequestItems ? crafItemsCanBeSelected : craftList).length) {
                    onCraftSelect(hasPendingRequestItems ? crafItemsCanBeSelected : craftList);
                  } else {
                    onCraftSelect([]);
                  }
                }}
                moveEntireCrew={moveEntireCrew}
                data-testid="craft-select-all"
                css={disableSelectAll ? 'opacity:0.5; pointer-events:none;' : ''}
              >
                <Box alignItems={{ sm: 'center' }}>
                  <Box mr={{ sm: '600' }}>
                    <Checkbox
                      css={moveEntireCrew ? 'opacity:0.5' : ''}
                      checked={
                        (selectedCrafts.length !== 0 &&
                          (hasPendingRequestItems ? crafItemsCanBeSelected : craftList).length) ===
                        selectedCrafts.length
                      }
                      indeterminate={
                        selectedCrafts.length > 0 &&
                        selectedCrafts.length < (hasPendingRequestItems ? crafItemsCanBeSelected : craftList).length
                      }
                      readOnly
                    />
                  </Box>
                  <Text size="400" weight="600" color="bechtel.grey.1000">
                    {t`Select All`} {` (${craftList.length})`}
                  </Text>
                </Box>
              </ListSelectionHeadingStyled>
            )}
            <Box flexDirection={{ sm: 'column' }} style={{ overflowY: 'auto' }}>
              {craftList.map((item) => {
                return (
                  <CraftListItem
                    key={item.id}
                    craftItem={item}
                    moveEntireCrew={moveEntireCrew}
                    hasPendingEntireCrewChangeRequest={hasPendingEntireCrewChangeRequest}
                    onCraftSelect={onCraftSelect}
                    selectedCrafts={selectedCrafts}
                  />
                );
              })}
            </Box>
          </Box>
        ))}
    </>
  );
};
